import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { selectUserInformations } from "../../features/authSlice";
import { API } from "../../shared/api/Api";
import { showToast } from "../../utils/utils";
import { formatPhoneNumberForDisplay } from "../../utils/phoneUtils";
import InstructorHistory from "../../components/Instructor/InstructorHistory";
import Loader from "../../components/Live/Loader";
import Pagination from "../../components/reusable/Pagination";
import {
  FiSearch,
  FiUser,
  FiArrowLeft,
  FiPhone,
  FiHash,
  FiBriefcase,
  FiX,
  FiChevronRight,
  FiActivity,
} from "react-icons/fi";

const DEBOUNCE_DELAY = 500;

const InstructorHistorySearchPage = () => {
  const user = useSelector(selectUserInformations);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [instructorWithHistory, setInstructorWithHistory] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      if (search) {
        setCurrentPage(1);
        setHasSearched(true);
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [search]);

  // Fetch instructors
  const fetchInstructors = useCallback(async () => {
    if (!debouncedSearch.trim()) {
      setInstructors([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(API.Instructors.app_instructors_all, {
        params: {
          page: currentPage,
          search: debouncedSearch,
          limit: 10,
        },
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (data.status === 200 && data.errorCode === 0) {
        setInstructors(data.data.items || []);
        setTotalPages(data.data.last_page || 1);
      } else {
        showToast("Error", data.message || "Failed to search instructors", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to search instructors", "error");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, user.token]);

  useEffect(() => {
    fetchInstructors();
  }, [fetchInstructors]);

  // Fetch full instructor data with history
  const fetchInstructorWithHistory = useCallback(async (instructorId) => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API.Instructors.app_instructors_get}/${instructorId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (data.status === 200 && data.errorCode === 0) {
        setInstructorWithHistory(data.data);
      } else {
        showToast("Error", data.message || "Failed to fetch instructor history", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to fetch instructor history", "error");
    } finally {
      setHistoryLoading(false);
    }
  }, [user.token]);

  const handleSelectInstructor = (instructor) => {
    setSelectedInstructor(instructor);
    setInstructorWithHistory(null);
    fetchInstructorWithHistory(instructor.id);
  };

  const handleBackToSearch = () => {
    setSelectedInstructor(null);
    setInstructorWithHistory(null);
  };

  const clearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    setInstructors([]);
    setHasSearched(false);
    setSelectedInstructor(null);
    setInstructorWithHistory(null);
  };

  return (
    <div className="min-h-screen p-2 sl:pt-0 sm:p-2 md:p-4 sl:p-6">
      <div className="w-full max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard/instructors"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
            >
              <FiArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                <FiActivity className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  Instructor History Search
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Search for an instructor to view their complete teaching history
                </p>
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!selectedInstructor ? (
            <motion.div
              key="search"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              {/* Search Input */}
              <div className="relative max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by Instructor ID, First Name, Last Name, Specialization..."
                    className="w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-all"
                  />
                  {search && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  Start typing to search for instructors
                </p>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex justify-center py-12">
                  <Loader />
                </div>
              )}

              {/* Search Results */}
              {!loading && instructors.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Search Results ({instructors.length} instructors found)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {instructors.map((instructor, index) => (
                      <motion.div
                        key={instructor.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleSelectInstructor(instructor)}
                        className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-2 border-transparent hover:border-purple-500 group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                              <FiUser className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {instructor.first_name} {instructor.last_name}
                              </h4>
                              <div className="flex items-center gap-4 mt-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                  <FiHash className="w-3 h-3" />
                                  ID: {instructor.id}
                                </span>
                                {instructor.mobile && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                    <FiPhone className="w-3 h-3" />
                                    {formatPhoneNumberForDisplay(instructor.mobile)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-500 transition-colors" />
                        </div>
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          {instructor.specialization && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <FiBriefcase className="w-3 h-3" />
                              {instructor.specialization}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            instructor.present
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                          }`}>
                            {instructor.present ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center mt-6">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* No Results */}
              {!loading && hasSearched && debouncedSearch && instructors.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiSearch className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No instructors found
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Try searching with different keywords
                  </p>
                </div>
              )}

              {/* Initial State */}
              {!loading && !hasSearched && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiUser className="w-10 h-10 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Search for an Instructor
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    Enter an instructor's ID, name, or specialization to view their complete teaching history, 
                    including courses, lessons, materials, and exams.
                  </p>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Selected Instructor Header */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleBackToSearch}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700"
                    >
                      <FiArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                      <FiUser className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {selectedInstructor.first_name} {selectedInstructor.last_name}
                      </h2>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FiHash className="w-4 h-4" />
                          Instructor ID: {selectedInstructor.id}
                        </span>
                        {selectedInstructor.mobile && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FiPhone className="w-4 h-4" />
                            {formatPhoneNumberForDisplay(selectedInstructor.mobile)}
                          </span>
                        )}
                        {selectedInstructor.specialization && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FiBriefcase className="w-4 h-4" />
                            {selectedInstructor.specialization}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/dashboard/instructors/${selectedInstructor.id}`}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    View Full Profile
                  </Link>
                </div>
              </div>

              {/* Instructor History Component */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <InstructorHistory instructorData={instructorWithHistory} loading={historyLoading} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default InstructorHistorySearchPage;
